import { BaseText } from '@/src/components/BaseText'
import { BaseButton } from '@/src/components/buttons/BaseButton'
import { DestructiveButton } from '@/src/components/buttons/DestructiveButton'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Divider,
  ModalCloseButton,
} from '@chakra-ui/react'
import { type FC, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface ISimpleConfirmDialog {
  isOpen: boolean
  acceptLabel: string
  title: string
  body: JSX.Element
  cancelLabel?: string
  actionButtonColor?: 'green' | 'red' | 'blue'
  onClose: () => void
  onAccept: () => void
  onCancel?: () => void
  isLoading?: boolean
  /**
   * Set when the confirm action only discards local, unsaved state rather than mutating server data,
   * so read-only users are not locked out of the dialog. Only relevant for `actionButtonColor='red'`.
   */
  ignoresReadonly?: boolean
}

export const ActionConfirmModal: FC<ISimpleConfirmDialog> = ({
  isOpen,
  acceptLabel,
  body = <></>,
  title,
  actionButtonColor,
  cancelLabel,
  onClose,
  onCancel,
  onAccept,
  isLoading,
  ignoresReadonly,
}) => {
  const { t } = useTranslation()
  const cancelRef = useRef()

  return (
    <AlertDialog
      isCentered
      isOpen={isOpen}
      // @ts-ignore
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      size='xl'
    >
      <AlertDialogOverlay>
        <AlertDialogContent minWidth={'513px'}>
          <AlertDialogHeader mb={'-16px'}>
            <BaseText variant='title'> {title}</BaseText>
            <ModalCloseButton
              // @ts-ignore
              ref={cancelRef}
              _focus={{}}
            />
          </AlertDialogHeader>

          <AlertDialogBody w='fit-content' mt='24px' mb={'42px'}>
            {body}
          </AlertDialogBody>

          <Divider />

          <AlertDialogFooter gap='10px' py='12px'>
            {(cancelLabel || onCancel) && (
              <BaseButton
                variant='secondary'
                onClick={() => {
                  onClose()
                  if (onCancel) {
                    onCancel()
                  }
                }}
              >
                {cancelLabel ?? t('common.cancel')}
              </BaseButton>
            )}

            {actionButtonColor === 'red' && (
              <DestructiveButton isLoading={isLoading} text={acceptLabel} onClick={onAccept} ignoresReadonly={ignoresReadonly} />
            )}
            {actionButtonColor === 'green' && (
              <BaseButton isLoading={isLoading} onClick={onAccept} variant='secondary' colorScheme={actionButtonColor}>
                {acceptLabel}
              </BaseButton>
            )}
            {actionButtonColor === 'blue' && (
              <BaseButton isLoading={isLoading} onClick={onAccept} variant='primary'>
                {acceptLabel}
              </BaseButton>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
